import { forEach, groupBy, keyBy, mapValues } from 'lodash'

import * as mutationTypes from './mutation-types'
import * as communicationMutationTypes from '@app/application/communication/sections/communication/store/mutation-types'

import remarkApi from '@app/application/communication/sections/communication/api/remark'
import communicationApi from '@app/application/communication/sections/communication/api/index'

const InboxCommunicationItems = (endpointParams) => ({
  state() {
    return {
      communicationItem: null,
      communicationItemLastRequestedAt: null,
    }
  },

  getters: {
    // remarkById is used in remark reconciliation proposal
    remarkById:
      ({ communicationItem }) =>
      (id) =>
        id === communicationItem.id ? communicationItem : null,
    communicationItem: ({ communicationItem }) => communicationItem,
  },

  mutations: {
    [mutationTypes.SET_COMMUNICATION_ITEM](state, { communicationItem }) {
      state.communicationItem = communicationItem
    },
    [mutationTypes.RESET_COMMUNICATION_ITEM](state) {
      state.communicationItem = null
    },
    [mutationTypes.SET_COMMUNICATION_ITEM_DONE](
      state,
      { done, doneAt, doneByName }
    ) {
      state.communicationItem.done = done
      state.communicationItem.doneAt = doneAt
      state.communicationItem.doneByName = doneByName
    },
    [mutationTypes.SET_COMMUNICATION_ITEM_LAST_REQUESTED_AT](state, timestamp) {
      state.communicationItemLastRequestedAt = timestamp
    },
  },
  actions: {
    fetchCommunicationItem(
      { commit, rootState, state },
      { id, type = 'Remark' }
    ) {
      commit(mutationTypes.SET_LOADING, {
        type: 'communicationItem',
        value: true,
      })
      // keep track of when we requested this item
      const requestedAt = new Date().getTime()
      commit(
        mutationTypes.SET_COMMUNICATION_ITEM_LAST_REQUESTED_AT,
        requestedAt
      )

      const companyId =
        rootState.notifications.activeCompanyId || Silverfin.currentCompanyId

      return new Promise((resolve) => {
        communicationApi
          .getCommunication({
            ...endpointParams,
            communicationItemType: type,
            communicationItemId: id,
            companyId,
          })
          .then(({ communication }) => {
            // if a slow request comes in, but the user has since requested another comms item, don't override the active comms item
            if (state.communicationItemLastRequestedAt > requestedAt) {
              resolve()
              return
            }
            // response can have one todo, one note or one check_list
            let communicationItem
            if (communication.notes.length > 0) {
              communicationItem = { ...communication.notes[0], type: 'Remark' }
            } else if (communication.todos.length > 0) {
              communicationItem = { ...communication.todos[0], type: 'Remark' }
            } else if (communication.checks.length > 0) {
              communicationItem = {
                ...communication.checks[0],
                type: 'CheckList',
              }
            }

            commit(mutationTypes.SET_COMMUNICATION_ITEM, { communicationItem })
            commit(mutationTypes.SET_LOADING, {
              type: 'communicationItem',
              value: false,
            })

            // index check ids for check lists on the front-end for now
            const checkLists = mapValues(
              groupBy(communication.checkLists, 'ledgerId'),
              (v) => keyBy(v, 'id')
            )
            forEach(checkLists, (clGroup) =>
              forEach(clGroup, (cl) => {
                cl['checkIds'] = communication.checks
                  .filter((check) => check.checkListId === cl.id)
                  .map((check) => check.id)
              })
            )
            commit(communicationMutationTypes.SET_CHECK_LISTS, checkLists)
            commit(
              communicationMutationTypes.SET_CHECKS,
              keyBy(communication.checks, 'id')
            )
            commit(
              communicationMutationTypes.SET_CHECKINS,
              keyBy(communication.checkins, 'id')
            )

            commit(
              communicationMutationTypes.SET_USERS,
              keyBy(communication.users, 'id')
            )
            commit(
              communicationMutationTypes.SET_ATTACHMENTS,
              keyBy(communication.attachments, 'id')
            )
            commit(
              communicationMutationTypes.SET_COMMENTS,
              keyBy(communication.comments, 'id')
            )

            const allRemarkTags = keyBy(communication.allRemarkTags, 'id')
            commit(
              communicationMutationTypes.SET_ALL_REMARK_TAGS,
              allRemarkTags
            )
            // key contexts by type (Account, ReconciliationText...) and then by id
            // to avoid id collisions of different type
            const contexts = mapValues(
              groupBy(communication.contexts, 'ledgerId'),
              (v) => mapValues(groupBy(v, 'type'), (v) => keyBy(v, 'id'))
            )
            commit(communicationMutationTypes.SET_CONTEXTS, contexts)

            resolve()
          })
      })
    },
    async toggleTodoDone({ commit, state }, todoId) {
      const companyId = state.communicationItem.companyId
      const response = await remarkApi.toggleTodoDone(todoId, companyId)
      commit(mutationTypes.SET_COMMUNICATION_ITEM_DONE, {
        done: response.done,
        doneAt: response.doneAt,
        doneByName: response.doneByName,
      })
    },
  },
})

export default InboxCommunicationItems
